/**
 * Os painéis do escritório 2D — a parte SOCIAL que é React e não Phaser.
 *
 * Regra da divisão, a mesma do resto do escritório: o que se desenha DENTRO do
 * mundo (balões, anéis de conversa) é Phaser, a 60fps, lido imperativamente do
 * store; o que é INTERFACE por cima do mundo (bater à porta, ver quem está
 * onde, escrever uma linha) é React, reativo, e mexe-se com o rato. Cada peça aqui lê o store por hook e fala com o servidor
 * pelo `gatewayClient()` — nenhuma delas guarda estado de sala próprio, porque
 * o dono desse estado é o servidor e a verdade chega sempre por evento.
 *
 * Vive fora do `OfficeSpace` para o loop do Phaser não ter de dividir ficheiro
 * com formulários: são dois mundos com ritmos diferentes (um a 60fps sem
 * render, outro a re-renderizar só quando alguém carrega num botão).
 */

import { ROOMS, roomAt, roomName, type Snowflake } from "@greenvoice/shared";
import { useEffect } from "react";

import { useVoiceSnapshot } from "../hooks/useVoice";
import { voiceEngine } from "../lib/voice";
import { type AppState, gatewayClient, useStore } from "../store/store";
import { Avatar } from "./Avatar";
import { IconLock, IconMic, IconMicOff } from "./Icons";

/** O nome por que se trata alguém — o mesmo critério das etiquetas dos avatares. */
function personName(state: AppState, userId: Snowflake): string {
  const u = state.users[userId];
  return u ? (u.displayName ?? u.username) : "alguém";
}

/** O nome de uma sala a partir do id — "sala" quando o id não é de nenhuma. */
function salaChamada(roomId: string): string {
  return roomName(roomId) ?? "sala";
}

// ── bater à porta ───────────────────────────────────────────────────────────

/**
 * As batidas à porta: as que me chegam (estou dentro, alguém quer entrar) e a
 * minha (bati e espero). A mesma entrada do store serve as duas vistas — quem
 * bateu e quem recebe olham para lados diferentes da mesma notificação.
 *
 * "Abrir" manda uma só coisa: `answerKnock(accept: true)`. É o SERVIDOR que
 * destranca a porta a seguir e publica o `RoomLockUpdate` — aceitar É abrir.
 * Mandar também um `lockRoom(false)` daqui era duplicar a decisão em dois
 * pacotes que podiam chegar trocados.
 *
 * Recebe o `spaceId` e não o `serverId` — é a chave por que as batidas chegam e
 * a chave por que a resposta volta. Hoje os dois valores são o MESMO texto (o
 * espaço do escritório É o servidor nu), e o nome do argumento fica assim
 * mesmo: houve um segundo mundo com espaço próprio, e é o `spaceId` que separa
 * batidas de espaços diferentes se algum dia voltar a haver outro.
 */
export function KnockPanel({ spaceId, insideRoomId }: { spaceId: Snowflake; insideRoomId: string | null }) {
  const knocks = useStore((s) => s.knocks);
  const selfId = useStore((s) => s.self?.id ?? null);
  const pruneKnocks = useStore((s) => s.pruneKnocks);

  // As batidas expiram por tempo, e ninguém manda um evento "expirou": é este
  // tique que as faz sair do ecrã. Só escreve no store quando corta alguma.
  useEffect(() => {
    const timer = window.setInterval(() => pruneKnocks(), 1_000);
    return () => window.clearInterval(timer);
  }, [pruneKnocks]);

  const incoming = knocks.filter(
    (k) => k.spaceId === spaceId && k.userId !== selfId && k.roomId === insideRoomId && k.accept === null,
  );
  const outgoing = knocks.filter((k) => k.spaceId === spaceId && k.userId === selfId);
  if (incoming.length === 0 && outgoing.length === 0) return null;

  return (
    <div className="office__knocks">
      {incoming.map((k) => (
        <IncomingKnock key={`in:${k.roomId}:${k.userId}`} spaceId={spaceId} roomId={k.roomId} userId={k.userId} />
      ))}
      {outgoing.map((k) => (
        <div key={`out:${k.roomId}`} className={`office__knock office__knock--mine${k.accept ? " is-open" : ""}`}>
          <span className="office__knocktext">
            {k.accept === null
              ? `Bateste à porta da ${salaChamada(k.roomId)} — à espera de resposta…`
              : k.accept
                ? `A porta da ${salaChamada(k.roomId)} abriu-se. Entra.`
                : `Agora não podem na ${salaChamada(k.roomId)}.`}
          </span>
        </div>
      ))}
    </div>
  );
}

function IncomingKnock({ spaceId, roomId, userId }: { spaceId: Snowflake; roomId: string; userId: Snowflake }) {
  const user = useStore((s) => s.users[userId]);
  const name = useStore((s) => personName(s, userId));
  const answer = (accept: boolean) => gatewayClient()?.answerKnock(spaceId, roomId, userId, accept);

  return (
    <div className="office__knock">
      {user ? <Avatar user={user} size={28} /> : null}
      <span className="office__knocktext">
        <strong>{name}</strong> está a bater à porta.
      </span>
      <button type="button" className="office__knockbtn office__knockbtn--yes" onClick={() => answer(true)}>
        Abrir
      </button>
      <button type="button" className="office__knockbtn" onClick={() => answer(false)}>
        Agora não
      </button>
    </div>
  );
}

// ── quem está onde ──────────────────────────────────────────────────────────

interface Person {
  id: Snowflake;
  name: string;
  self: boolean;
}

/**
 * As salas que esta lista mostra. O escritório tem uma sala SEM nome (o
 * corredor, que é sala para efeitos de geometria mas não é sítio nenhum para
 * efeitos de lista) — a exclusão sai da geometria, não de uma segunda lista a
 * manter em sincronia.
 */
const SALAS_LISTADAS = ROOMS.filter((r) => r.name !== "");

/**
 * O painel lateral: cada sala, quem lá está, e um clique para ir ter com essa
 * pessoa (o avatar faz o caminho sozinho, ver `pathToPerson`).
 *
 * As salas VAZIAS também aparecem — saber que a Sala de Reunião está livre é
 * metade da razão para abrir isto. Quem está no corredor, no jardim ou no
 * estacionamento cai todo no mesmo grupo do fim, que só aparece se tiver gente.
 *
 * As trancas entram pela mesma busca das pessoas — o store guarda-as por
 * `spaceId`, e o cadeado ao lado da Direcção sai daqui sem um `if` extra.
 */
export function WhoPanel({
  serverId,
  selfRoomId,
  onGoTo,
  onClose,
}: {
  serverId: Snowflake;
  selfRoomId: string | null;
  onGoTo: (userId: Snowflake) => void;
  onClose: () => void;
}) {
  // O espaço do escritório É o servidor nu.
  const spaceId = serverId;
  const positions = useStore((s) => s.positions[spaceId]);
  const users = useStore((s) => s.users);
  const self = useStore((s) => s.self);
  const locks = useStore((s) => s.roomLocks[spaceId]);

  // A sala de cada um sai da posição que já circula na rede — não há nenhuma
  // mensagem "estou na sala X" a inventar. `roomAt` é a mesma função que o loop
  // e o servidor usam, por isso a lista nunca discorda do mundo.
  const byRoom = new Map<string | null, Person[]>();
  const put = (roomId: string | null, person: Person) => {
    const list = byRoom.get(roomId);
    if (list) list.push(person);
    else byRoom.set(roomId, [person]);
  };
  if (self) put(selfRoomId, { id: self.id, name: self.displayName ?? self.username, self: true });
  for (const occupant of Object.values(positions ?? {})) {
    const u = users[occupant.userId];
    // `roomAt` e não `roomOrDoorway`: quem está parado no vão está a passar, não
    // está na sala — e nesta lista o que interessa é onde a pessoa ESTÁ, não em
    // que call caiu.
    put(roomAt(occupant.x, occupant.y)?.id ?? null, {
      id: occupant.userId,
      name: u ? (u.displayName ?? u.username) : "alguém",
      self: false,
    });
  }
  for (const list of byRoom.values()) list.sort((a, b) => a.name.localeCompare(b.name, "pt"));

  const outside = byRoom.get(null) ?? [];

  return (
    <aside className="office__who">
      <header className="office__whohead">
        <h2>Quem está onde</h2>
        <button type="button" className="office__whoclose" onClick={onClose} aria-label="Fechar o painel">
          ×
        </button>
      </header>
      <div className="office__wholist">
        {SALAS_LISTADAS.map((room) => {
          const people = byRoom.get(room.id) ?? [];
          const locked = locks?.[room.id] !== undefined;
          return (
            <section key={room.id} className={`office__whoroom${people.length === 0 ? " is-empty" : ""}`}>
              <h3>
                {locked ? <IconLock size={11} /> : null}
                {room.name}
                <span className="office__whocount">{people.length || "livre"}</span>
              </h3>
              {people.map((p) => (
                <PersonRow key={p.id} person={p} onGoTo={onGoTo} />
              ))}
            </section>
          );
        })}
        {outside.length > 0 ? (
          <section className="office__whoroom">
            <h3>
              Corredor e exterior
              <span className="office__whocount">{outside.length}</span>
            </h3>
            {outside.map((p) => (
              <PersonRow key={p.id} person={p} onGoTo={onGoTo} />
            ))}
          </section>
        ) : null}
      </div>
      <footer className="office__whofoot">Carrega num nome para ires ter com essa pessoa.</footer>
    </aside>
  );
}

function PersonRow({ person, onGoTo }: { person: Person; onGoTo: (userId: Snowflake) => void }) {
  const user = useStore((s) => s.users[person.id]);
  return (
    <button
      type="button"
      className={`office__whoperson${person.self ? " is-self" : ""}`}
      onClick={() => onGoTo(person.id)}
      disabled={person.self}
      title={person.self ? "És tu" : `Ir ter com ${person.name}`}
    >
      {user ? <Avatar user={user} size={22} showStatus={false} /> : <span className="office__whodot" />}
      <span>{person.name}</span>
      {person.self ? <span className="office__whoself">tu</span> : null}
    </button>
  );
}

// ── a barra do huddle de corredor ───────────────────────────────────────────

/**
 * O canto que aparece quando se está numa conversa de corredor.
 *
 * Não tem botão de sair de propósito: sai-se a andar. O huddle é geometria —
 * um botão "sair" punha-nos fora de uma conversa em que continuávamos de pé, e
 * no frame seguinte a regra voltava a meter-nos lá dentro. O que tem é o
 * microfone, que é a única coisa que não se resolve com os pés.
 */
export function HuddleBar() {
  const snapshot = useVoiceSnapshot();
  const users = useStore((s) => s.users);
  const selfId = useStore((s) => s.self?.id ?? null);

  const names = snapshot.participants.map((p) => {
    const u = users[p.identity];
    return {
      id: p.identity,
      speaking: p.speaking,
      name: p.identity === selfId ? "Tu" : u ? (u.displayName ?? u.username) : "alguém",
    };
  });

  return (
    <div className="office__huddle">
      <span className="office__huddletag">
        <span className="office__huddledot" />
        Conversa de corredor
      </span>
      <div className="office__huddlepeople">
        {names.length === 0 ? (
          <span className="office__huddleempty">a ligar…</span>
        ) : (
          names.map((p) => (
            <span key={p.id} className={`office__huddleperson${p.speaking ? " is-speaking" : ""}`}>
              {p.name}
            </span>
          ))
        )}
      </div>
      <button
        type="button"
        className={`office__huddlemic${snapshot.micEnabled ? "" : " is-off"}`}
        onClick={() => void voiceEngine.setMic(!snapshot.micEnabled)}
        title={snapshot.micEnabled ? "Silenciar" : "Reactivar o microfone"}
      >
        {snapshot.micEnabled ? <IconMic size={14} /> : <IconMicOff size={14} />}
      </button>
    </div>
  );
}
